import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isScapAvailable,
  listStorageFiles,
  CONTENT_PATH,
} from '../lib/scap.js';
import './Slideshow.css';

/** Image extensions supported in SCAP storage slider */
const IMAGE_EXT = new Set(
  ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].map((x) => x.toLowerCase())
);
/** Video extensions (optional; display via <video> if needed) */
const VIDEO_EXT = new Set(['mp4', 'webm', 'ogg'].map((x) => x.toLowerCase()));

function getExtension(name) {
  const i = (name || '').lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

/**
 * Build display URL for a file listed under pathURI on webOS Signage.
 * file://internal/content/foo.jpg -> http://127.0.0.1:9080/content/foo.jpg
 */
function storagePathToDisplayUrl(pathURI, fileName, baseUrl = 'http://127.0.0.1:9080') {
  const prefix = 'file://internal/';
  let segment = pathURI.startsWith(prefix) ? pathURI.slice(prefix.length).replace(/\/$/, '') : 'content';
  if (!segment) segment = 'content';
  const path = segment ? `${segment}/${fileName}` : fileName;
  return `${baseUrl.replace(/\/$/, '')}/${path}`;
}

export default function SignageSlider({
  storagePath = CONTENT_PATH,
  displayBaseUrl = 'http://127.0.0.1:9080',
  durationMs = 7000,
  includeVideo = true,
}) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  const items = files;
  const item = items[index];
  const isVideo =
    includeVideo && item && VIDEO_EXT.has(getExtension(item.name));
  const isImage = item && IMAGE_EXT.has(getExtension(item.name));
  const src = item
    ? storagePathToDisplayUrl(storagePath, item.name, displayBaseUrl)
    : '';

  const next = useCallback(() => {
    if (!items.length) return;
    setIndex((i) => (i + 1) % items.length);
  }, [items.length]);

  // Load list from SCAP Storage
  useEffect(() => {
    if (!isScapAvailable()) {
      setError('SCAP tidak tersedia (bukan lingkungan webOS Signage)');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    listStorageFiles(storagePath)
      .then((res) => {
        const list = (res?.files || [])
          .filter((f) => f.type === 'file')
          .filter(
            (f) =>
              IMAGE_EXT.has(getExtension(f.name)) ||
              (includeVideo && VIDEO_EXT.has(getExtension(f.name)))
          );
        setFiles(list);
        setIndex(0);
      })
      .catch((e) => {
        setError(e?.message || 'Gagal memuat daftar file');
        setFiles([]);
      })
      .finally(() => setLoading(false));
  }, [storagePath, includeVideo]);

  // Auto-advance timer (image slides only; video can use onEnded later)
  useEffect(() => {
    if (!items.length || isVideo) return;
    timerRef.current = setTimeout(next, durationMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [index, items.length, isVideo, durationMs, next]);

  if (!isScapAvailable()) {
    return (
      <div className="slideshow" data-spotlight-id="slideshow-stage">
        <div className="stage scap-message scap-unavailable">
          <p>SCAP tidak tersedia.</p>
          <p className="hint">Jalankan di webOS Signage untuk slider dari storage.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="slideshow" data-spotlight-id="slideshow-stage">
        <div className="stage scap-message scap-loading">Memuat daftar file dari storage…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="slideshow" data-spotlight-id="slideshow-stage">
        <div className="stage scap-message scap-error">
          <p>{error}</p>
          <p className="hint">Path: {storagePath}</p>
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="slideshow" data-spotlight-id="slideshow-stage">
        <div className="stage scap-message scap-empty">
          <p>Tidak ada file gambar atau video di storage.</p>
          <p className="hint">Path: {storagePath}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="slideshow" data-spotlight-id="slideshow-stage">
      <div className="stage slide-fade is-visible">
        {isVideo ? (
          <video
            key={src}
            className="slide"
            src={src}
            autoPlay
            muted
            playsInline
            onEnded={next}
            style={{ objectFit: 'contain', width: '100%', height: '100%' }}
          />
        ) : (
          <img
            key={src}
            className="slide"
            src={src}
            alt=""
            style={{ objectFit: 'contain', width: '100%', height: '100%' }}
          />
        )}
      </div>
    </div>
  );
}
